import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useEffect } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import ErrorBoundary from "@/components/ErrorBoundary";
import ReactGA from "react-ga4";
import Index from "./pages/Index";
import LandingPage from "./pages/LandingPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import HowItWorks from "./pages/HowItWorks";
import ScriptAnalysisPage from "./pages/ScriptAnalysisPage";
import ProjectIntakePage from "./pages/ProjectIntakePage";
import { Dashboard } from "./pages/Dashboard";
import YouTubeCallback from "./pages/YouTubeCallback";
import NotFound from "./pages/NotFound";
import HardcodedResultsPage from "./pages/HardcodedResultsPage";
import ResultsPage from "./pages/ResultsPage";
import ArtieScorePage from "./pages/ArtieScorePage";
import PlacementInventoryPage from "./pages/PlacementInventoryPage";
import PlacementLiveRunPage from "./pages/PlacementLiveRunPage";
import StoredPlacementPage from "./pages/StoredPlacementPage";
import ScorePendingPage from "./pages/ScorePendingPage";
import UploadThanksPage from "./pages/UploadThanksPage";
import ProjectDetailsPage from "./pages/ProjectDetailsPage";
import ProjectDetailsInputPage from "./pages/ProjectDetailsInputPage";
import ProjectCreationPage from "./pages/ProjectCreationPage";
import SignupPage from "./pages/SignupPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { SharedScorePage } from "./pages/SharedScorePage";
import { Header } from "./components/Header";
import { useAuthStore } from "@/stores/authStore";
import { OnboardingProvider } from "@/context/OnboardingContext";
import { BrandOnboardingProvider } from "@/context/BrandOnboardingContextFixed";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Onboarding1 from "./pages/Onboarding1";
import Onboarding2 from "./pages/Onboarding2";
import Onboarding3 from "./pages/Onboarding3";
import Onboarding4 from "./pages/Onboarding4";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import OnboardingStep1 from "./pages/onboarding/OnboardingStep1";
import OnboardingStep2 from "./pages/onboarding/OnboardingStep2";
import OnboardingStep3 from "./pages/onboarding/OnboardingStep3";
import OnboardingStep4 from "./pages/onboarding/OnboardingStep4";
import BrandOnboardingStep1Fixed from "./pages/brand-onboarding/BrandOnboardingStep1Fixed";
import BrandOnboardingStep2Fixed from "./pages/brand-onboarding/BrandOnboardingStep2Fixed";
import BrandOnboardingStep3Fixed from "./pages/brand-onboarding/BrandOnboardingStep3Fixed";
import BrandOnboardingStep4Fixed from "./pages/brand-onboarding/BrandOnboardingStep4Fixed";
import BrandDashboard from "./pages/BrandDashboard";
import BookingWindowsPage from "./pages/campaigns/BookingWindowsPage";
import CampaignsListPage from "./pages/campaigns/CampaignsListPage";
import CreateCampaignPage from "./pages/campaigns/CreateCampaignPage";
import CampaignDetailPage from "./pages/campaigns/CampaignDetailPage";
import BrowseEligibleProjectsPage from "./pages/campaigns/BrowseEligibleProjectsPage";
import BrowsePackagesPage from "./pages/campaigns/BrowsePackagesPage";
import HoldPlacedPage from "./pages/campaigns/HoldPlacedPage";
import CampaignProjectsPage from "./pages/campaigns/CampaignProjectsPage";
import CampaignProjectReportPage from "./pages/campaigns/CampaignProjectReportPage";
import PackageReadyPage from "./pages/campaigns/PackageReadyPage";
import RequirementsSubmissionPage from "./pages/brand-dashboard/RequirementsSubmission/RequirementsSubmissionPage";
import OnboardingV2 from "./pages/onboarding-v2/OnboardingV2";
import OnboardingV2Page2 from "./pages/onboarding-v2/OnboardingV2Page2";
import OnboardingV2Page3 from "./pages/onboarding-v2/OnboardingV2Page3";
import OnboardingV2Page3_2 from "./pages/onboarding-v2/OnboardingV2Page3_2";
import OnboardingV2Page4 from "./pages/onboarding-v2/OnboardingV2Page4";
import LoadingV2 from "./pages/onboarding-v2/LoadingV2";
import InitialAssessmentPage from "./pages/InitialAssessmentPage";
import NewProjectStep1 from "./pages/project-creation/NewProjectStep1";
import NewProjectStep2 from "./pages/project-creation/NewProjectStep2";
import NewProjectStep3 from "./pages/project-creation/NewProjectStep3";
import NewProjectLoading from "./pages/project-creation/NewProjectLoading";
import NewProjectAudience from "./pages/project-creation/NewProjectAudience";
import NewProjectTeam from "./pages/project-creation/NewProjectTeam";
import { ProjectCreationLayout } from "./pages/project-creation/ProjectCreationLayout";
import { AdminRoute } from "@/components/AdminRoute";
import ProfilePage from "./pages/ProfilePage";
import { AdminLayout } from "@/components/admin/AdminLayout";
import UsersPage from "@/pages/admin/UsersPage";
import ProjectsPage from "@/pages/admin/ProjectsPage";
import AdminProjectDetailsPage from "@/pages/admin/ProjectDetailsPage";
import EarlyAccessPage from "@/pages/admin/EarlyAccessPage";
import TacticManagementPage from "@/pages/admin/TacticManagementPage";
import MonitoringPage from "@/pages/admin/MonitoringPage";
import BookingWindowsListPage from "@/pages/admin/BookingWindowsListPage";
import CreateBookingWindowPage from "@/pages/admin/CreateBookingWindowPage";
import BookingWindowDetailPage from "@/pages/admin/BookingWindowDetailPage";
import ScoreLayout from "./components/score/ScoreLayout";
import { ReachPage } from "./pages/score/ReachPage";
import { BrandFitPage } from "./pages/score/BrandFitPage";
import { ProductionReadinessPage } from "./pages/score/ProductionReadinessPage";
import { PitchDeckPage } from "./pages/score/PitchDeckPage";
import WelcomeBackPage from "./pages/project-v2/WelcomeBackPage";
import ScriptFeedbackPage from "./pages/project-v2/ScriptFeedbackPage";
import ScoreV2Page from "./pages/project-v2/ScoreV2Page";
import TaskPage from "./pages/project-v2/TaskPage";
import TaskDetailPage from "./pages/project-v2/TaskDetailPage";
import TaskDonePage from "./pages/project-v2/TaskDonePage";
import ProjectDetailsV2Page from "./pages/project-v2/ProjectDetailsV2Page";
import ChatbotPage from "./pages/project-v2/ChatbotPage";
import ProjectV2Router from "./components/ProjectV2Router";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CareersPage from "./pages/CareersPage";
import InvestorsPage from "./pages/InvestorsPage";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsPage from "./pages/AnalyticsPage";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import CollaboratorOnboardingPage from "./pages/CollaboratorOnboardingPage";
import ConsentPage from "./pages/ConsentPage";
import CreatorAgreementPage from "./pages/CreatorAgreementPage";
import BrandAgreementPage from "./pages/BrandAgreementPage";
import ClarityTracking from "@/components/ClarityTracking";
import { logger } from "@/lib/logger";

// Ops Dashboard imports
import OpsDashboardPage from "./pages/ops-dashboard/OpsDashboardPage";
import CampaignCreationPage from "./pages/brand-marketplace/CampaignCreationPage";
import BrowseInventoryPage from "./pages/brand-marketplace/BrowseInventoryPage";
import BrandMatchPage from "./pages/brand-marketplace/BrandMatchPage";
import ViewPackagePage from "./pages/brand-marketplace/ViewPackagePage";
import BrandMarketplaceAccessPage from "./pages/brand-marketplace/BrandMarketplaceAccessPage";

// Configure QueryClient with better error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (
          error?.message?.includes("401") ||
          error?.message?.includes("403")
        ) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry mutations on validation errors
        if (
          error?.message?.includes("422") ||
          error?.message?.includes("400")
        ) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

const AppContent = () => {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleSessionExpired = () => {
      navigate('/login?session_expired=true');
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const hideSidebarPaths = [
    "/",
    "/dashboard",
    "/analytics",
    "/login",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/onboarding/1",
    "/onboarding/2",
    "/onboarding/3",
    "/onboarding/4",
    "/onboarding/step1",
    "/onboarding/step2",
    "/onboarding/step3",
    "/onboarding/step4",
    "/brand-onboarding/company-info",
    "/brand-onboarding/brand-profile",
    "/brand-onboarding/complete",
    "/brand-dashboard",
    "/onboarding-v2",
    "/onboarding-v2/page2",
    "/onboarding-v2/page3",
    "/onboarding-v2/page3-2",
    "/onboarding-v2/page4",
    "/onboarding-v2/loading",
    "/projects/create/step-1",
    "/projects/create/step-2",
    "/projects/create/step-3",
    "/projects/create/audience",
    "/projects/create/team",
    "/projects/create/loading",
    "/initial-assessment",
    "/profile",
    "/privacy",
    "/terms",
    "/careers",
    "/investors",
    "/consent",
    "/project-v2/welcome-back",
    "/project-v2/script-feedback",
    "/project-v2/score-v2",
    "/project-v2/task-page",
    "/project-v2/task-detail-page",
    "/project-v2/task-done",
    "/project-v2/project-details-v2",
    "/project-v2/chatbot",
    "/projects/create/step-2",
  ];

  const showSidebar =
    !hideSidebarPaths.includes(location.pathname) &&
    !location.pathname.startsWith("/admin") &&
    !location.pathname.startsWith("/projects/create") &&
    !location.pathname.startsWith("/campaigns");

  return (
    <div className="flex min-h-screen bg-background w-full">
      <ScrollToTop />
      {showSidebar && <Header />}
      <main
        className={`flex-1 w-full transition-all duration-300 ease-in-out ${showSidebar ? "pt-16 md:pt-0" : ""}`}
      >
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/old-home" element={<Index />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          {/* Internal demo of the O2 placement agent, deliberately unauthenticated and
              unlinked from nav. /placements is a static snapshot with no backend at all;
              /placements/live uploads a script and runs the agent for real, and needs the
              backend up with PLACEMENT_DEMO_ENABLED set. */}
          <Route path="/placements" element={<PlacementInventoryPage />} />
          <Route path="/placements/live" element={<PlacementLiveRunPage />} />
          {/* Reads a stored inventory from the API. The route is public like its siblings, but
              the endpoint behind it requires a session and enforces ownership — the page
              renders a "sign in" state on 401 rather than the router hiding it, so the same
              URL is shareable with someone who does have access. */}
          <Route path="/placements/scripts/:scriptId" element={<StoredPlacementPage />} />
          <Route path="/share/:token" element={<SharedScorePage />} />
          <Route path="/invite/:token" element={<AcceptInvitationPage />} />
          <Route path="/invite/onboarding/:token" element={<CollaboratorOnboardingPage />} />
          <Route
            path="/integrations/youtube/callback"
            element={<YouTubeCallback />}
          />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/investors" element={<InvestorsPage />} />

          {/* Consent Route - Protected but accessible during onboarding */}
          <Route path="/consent" element={<ConsentPage />} />

          {/* Legal Document Pages */}
          <Route path="/creator-agreement" element={<CreatorAgreementPage />} />
          <Route path="/brand-agreement" element={<BrandAgreementPage />} />
          <Route path="/brand-agreement" element={<BrandAgreementPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            {/* New V2 Onboarding Flow */}
            <Route path="/onboarding/step1" element={<OnboardingStep1 />} />
            <Route path="/onboarding/step2" element={<OnboardingStep2 />} />
            <Route path="/onboarding/step3" element={<OnboardingStep3 />} />
            <Route path="/onboarding/step4" element={<OnboardingStep4 />} />
            
            {/* Brand Onboarding Flow */}
            <Route path="/brand-onboarding/company-info" element={<BrandOnboardingStep1Fixed />} />
            <Route path="/brand-onboarding/brand-profile" element={<BrandOnboardingStep2Fixed />} />
            <Route path="/brand-onboarding/complete" element={<BrandOnboardingStep4Fixed />} />
            <Route path="/brand-dashboard" element={<BrandDashboard />} />
            <Route path="/campaigns" element={<CampaignsListPage />} />
            <Route path="/campaigns/booking-windows" element={<BookingWindowsPage />} />
            <Route path="/campaigns/requirements" element={<RequirementsSubmissionPage />} />
            <Route path="/campaigns/create" element={<CreateCampaignPage />} />
            <Route path="/campaigns/:campaignId" element={<CampaignDetailPage />} />
            <Route path="/campaigns/:campaignId/browse-eligible" element={<BrowseEligibleProjectsPage />} />
            <Route path="/campaigns/:campaignId/packages" element={<BrowsePackagesPage />} />
            <Route path="/campaigns/:campaignId/hold-placed" element={<HoldPlacedPage />} />
            <Route path="/campaigns/:campaignId/projects" element={<CampaignProjectsPage />} />
            <Route path="/campaigns/:campaignId/projects/:projectId/report" element={<CampaignProjectReportPage />} />
            <Route path="/campaigns/:campaignId/package-ready" element={<PackageReadyPage />} />

            <Route path="/onboarding-v2" element={<OnboardingV2 />} />
            <Route
              path="/onboarding-v2/page2"
              element={<OnboardingV2Page2 />}
            />
            <Route
              path="/onboarding-v2/page3"
              element={<OnboardingV2Page3 />}
            />
            <Route
              path="/onboarding-v2/page3-2"
              element={<OnboardingV2Page3_2 />}
            />
            <Route
              path="/onboarding-v2/page4"
              element={<OnboardingV2Page4 />}
            />
            <Route path="/onboarding-v2/loading" element={<LoadingV2 />} />
            <Route
              path="/initial-assessment"
              element={<InitialAssessmentPage />}
            />

            {/* New Project Creation Flow */}
            <Route element={<ProjectCreationLayout />}>
              <Route path="/projects/create/step-1" element={<NewProjectStep1 />} />
              <Route path="/projects/create/step-2" element={<NewProjectStep2 />} />
              <Route path="/projects/create/step-3" element={<NewProjectStep3 />} />
              <Route path="/projects/create/audience" element={<NewProjectAudience />} />
              <Route path="/projects/create/team" element={<NewProjectTeam />} />
              <Route path="/projects/create/loading" element={<NewProjectLoading />} />
              <Route path="/initial-assessment" element={<InitialAssessmentPage />} />
            </Route>

            {/* Legacy Onboarding Routes (kept for backward compatibility) */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/onboarding/1" element={<Onboarding1 />} />
            <Route path="/onboarding/2" element={<Onboarding2 />} />
            <Route path="/onboarding/3" element={<Onboarding3 />} />
            <Route path="/onboarding/4" element={<Onboarding4 />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/script-analysis" element={<ScriptAnalysisPage />} />
            <Route path="/project-intake" element={<ProjectIntakePage />} />
            <Route
              path="/hardcoded-results"
              element={<HardcodedResultsPage />}
            />
            <Route path="/results/:scriptId" element={<ResultsPage />} />
            <Route
              path="/projects"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route path="/projects/new" element={<ProjectCreationPage />} />
            <Route path="/project-intake" element={<ProjectIntakePage />} />
            <Route
              path="/projects/new-details"
              element={<ProjectDetailsInputPage />}
            />
            <Route path="/projects/:projectId/reach" element={<ReachPage />} />
            <Route
              path="/projects/:projectId/brand-fit"
              element={<BrandFitPage />}
            />
            <Route
              path="/projects/:projectId/production-readiness"
              element={<ProductionReadinessPage />}
            />
            <Route path="/projects/:projectId" element={<ScoreLayout />}>
              <Route index element={<ProjectDetailsPage />} />
              <Route path="pitch-deck" element={<PitchDeckPage />} />
            </Route>
            <Route
              path="/artie-score/:projectId"
              element={<ArtieScorePage />}
            />
            <Route
              path="/projects/:projectId/pending"
              element={<ScorePendingPage />}
            />
            {/* Where a creator lands after uploading in inventory mode. Sits beside the pending
                route rather than replacing it: both waiting screens redirect here when the flag is
                on, and clearing the flag restores the old destinations untouched. */}
            <Route path="/upload-thanks" element={<UploadThanksPage />} />
            {/* Main project entry point - handles first-time vs returning user flow */}
            <Route
              path="/project-v2/:projectId"
              element={<ProjectV2Router />}
            />
            {/* Individual flow pages - accessible directly for navigation within the flow */}
            <Route
              path="/project-v2/welcome-back/:projectId"
              element={<WelcomeBackPage />}
            />
            <Route
              path="/project-v2/script-feedback/:projectId"
              element={<ScriptFeedbackPage />}
            />
            <Route path="/project-v2/score-v2/:projectId" element={<ScoreV2Page />} />
            <Route path="/project-v2/task-page/:projectId" element={<TaskPage />} />
            <Route
              path="/project-v2/task-detail-page/:projectId"
              element={<TaskDetailPage />}
            />
            <Route path="/project-v2/task-done/:projectId" element={<TaskDonePage />} />
            <Route
              path="/project-v2/project-details-v2/:projectId"
              element={<ProjectDetailsV2Page />}
            />
            <Route path="/project-v2/chatbot/:projectId" element={<ChatbotPage />} />
          </Route>

          {/* Admin Routes */}
          <Route element={<AdminRoute />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/users" replace />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route
                path="projects/:projectId"
                element={<AdminProjectDetailsPage />}
              />
              <Route path="early-access" element={<EarlyAccessPage />} />
              <Route path="tactics" element={<TacticManagementPage />} />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="booking-windows" element={<BookingWindowsListPage />} />
              <Route path="booking-windows/create" element={<CreateBookingWindowPage />} />
              <Route path="booking-windows/:id" element={<BookingWindowDetailPage />} />
              <Route path="booking-windows/:id/edit" element={<BookingWindowDetailPage />} />
              
              {/* Ops Dashboard Routes */}
              <Route path="ops-dashboard" element={<OpsDashboardPage />} />
              
              {/* Brand Marketplace Routes */}
              <Route path="brand-marketplace/campaign-creation" element={<CampaignCreationPage />} />
              <Route path="brand-marketplace/browse-inventory" element={<BrowseInventoryPage />} />
              {/* Ranked shows for a brand brief, from the placement inventory. Sits beside
                  browse-inventory rather than replacing it: that page is backed by the older
                  project-level matcher, and which of the two survives is an open decision. */}
              <Route path="brand-marketplace/match" element={<BrandMatchPage />} />
              <Route path="brand-marketplace/view-package/:packageId" element={<ViewPackagePage />} />
              <Route path="brand-marketplace/access/:brandId" element={<BrandMarketplaceAccessPage />} />
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
};

const App = () => {
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user._id,
        email: user.email,
        username: user.name,
      });
      // Also set user in our custom logger
      logger.setUser({
        id: user._id,
        email: user.email,
        name: user.name,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
      ReactGA.initialize(import.meta.env.VITE_GOOGLE_ANALYTICS_ID);
    }
  }, []);

  // Test logging functionality on app initialization
  useEffect(() => {
    logger.info("Frontend application initialized", {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Run logging test in development
    if (import.meta.env.DEV) {
      setTimeout(() => {
        logger.testLogging();
      }, 2000); // Delay to ensure Sentry is fully initialized
    }
  }, []);

  // Check if the site should show the coming soon page
  const showComingSoon = import.meta.env.VITE_SHOW_COMING_SOON === "true";

  if (showComingSoon) {
    return (
      <ErrorBoundary>
        <BrowserRouter>
          <ComingSoonPage />
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      {/* Add Clarity Tracking */}
      <ClarityTracking
        projectId={import.meta.env.VITE_CLARITY_PROJECT_ID || ""}
        // enabled={import.meta.env.PROD}
        enabled={import.meta.env.PROD}
        userId={user?._id}
        userEmail={user?.email}
        userName={user?.name}
      />
      <GoogleOAuthProvider
        clientId={
          import.meta.env.VITE_GOOGLE_CLIENT_ID || "dummy-id-to-prevent-crash"
        }
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <OnboardingProvider>
              <BrandOnboardingProvider>
                <BrowserRouter>
                  <AppContent />
                </BrowserRouter>
              </BrandOnboardingProvider>
            </OnboardingProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
};



export default App;
